import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { IContactFormWizardSettings, EWizardStep } from "./typings";
import { IWorkspaceMember } from "dto/IWorkspace";
import Avatar from "components/Avatar";
import Form from "../../../form/src/components/Form";
import NotaIcon from "assets/icons/nota.svg?react";
import { formatMailboxNumber } from "utils/formatMailboxNumber";
import styles from "./styles.module.scss";
import bizStyles from "./ContactFormWizardPreview.module.scss";
import sharingStyles from "./steps/Step2Sharing.module.scss";

interface Props {
  settings: IContactFormWizardSettings;
  members?: IWorkspaceMember[];
  activeStep?: EWizardStep;
}

const THEME_CLASSES: Record<number, string> = {
  0: styles.theme0,
  1: styles.theme1,
  2: styles.theme2,
  3: styles.theme3,
  4: styles.theme4,
  5: styles.theme5,
  6: styles.theme6,
};

const BusinessCardPreview = ({ settings }: { settings: IContactFormWizardSettings }) => {
  const auth = useAuth();
  const hasBusinessData = !!(settings.companyName || settings.nip);
  const showAddress = settings.shareAddress && !!settings.companyAddress;
  const privateFullName = auth.user?.profile?.name ?? "";
  const privateActive = settings.showPrivateName ?? false;
  const businessActive = settings.showBusinessCard ?? false;
  const privateMailbox = formatMailboxNumber(settings.privateMailboxNumber);
  const businessMailbox = formatMailboxNumber(settings.businessMailboxNumber);

  const privateCardCls = `${bizStyles.bizCard} ${!privateActive ? bizStyles.bizCardInactive : ""}`;
  const businessCardCls = `${bizStyles.bizCard} ${!businessActive ? bizStyles.bizCardInactive : ""}`;

  return (
    <div className={bizStyles.dualRoot}>
      <div className={bizStyles.cardSection}>
        <div className={bizStyles.cardSectionLabel}>Profil prywatny</div>
        <div className={privateCardCls}>
          <div className={bizStyles.bizStripe} />
          <div className={bizStyles.bizBody}>
            {privateFullName ? (
              <div className={bizStyles.bizName}>{privateFullName}</div>
            ) : (
              <div className={bizStyles.bizEmpty}>Imię i nazwisko (z profilu konta)</div>
            )}
            {privateMailbox && (
              <div className={bizStyles.bizMailboxRow}>
                <span className={bizStyles.bizMailboxLabel}>Nr skrzynki eSzyfrowane:</span>
                <span className={bizStyles.bizMailboxNumber}>{privateMailbox}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={bizStyles.cardSection}>
        <div className={bizStyles.cardSectionLabel}>Profil biznesowy</div>
        <div className={businessCardCls}>
          <div className={bizStyles.bizStripe} />
          <div className={bizStyles.bizBody}>
            {!hasBusinessData ? (
              <div className={bizStyles.bizEmpty}>
                Uzupełnij NIP, aby zobaczyć podgląd wizytówki
              </div>
            ) : (
              <>
                <div className={bizStyles.bizName}>{settings.companyName}</div>
                <div className={bizStyles.bizNip}>NIP: {settings.nip}</div>
                {showAddress && (
                  <div className={bizStyles.bizAddress}>{settings.companyAddress}</div>
                )}
                {businessMailbox && (
                  <div className={bizStyles.bizMailboxRow}>
                    <span className={bizStyles.bizMailboxLabel}>Nr skrzynki eSzyfrowane:</span>
                    <span className={bizStyles.bizMailboxNumber}>{businessMailbox}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SharingPreview = ({ settings }: { settings: IContactFormWizardSettings }) => {
  const shareInternal = settings.shareInternal ?? false;
  const sharePublic = settings.sharePublic ?? false;

  return (
    <div className={bizStyles.bizRoot}>
      <div className={sharingStyles.scopeGraphic} style={{ width: "100%", maxWidth: 400 }}>
        <div className={`${sharingStyles.scopeCard} ${shareInternal ? sharingStyles.active : ""}`}>
          <div className={sharingStyles.scopeIcon}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
            </svg>
          </div>
          <div className={sharingStyles.scopeTitle}>Udostępnienie wewnętrzne</div>
          <div className={sharingStyles.scopeDesc}>Widoczna tylko dla członków Twojego zespołu w NotaUp</div>
        </div>

        <div className={sharingStyles.scopeDivider}>
          <div className={sharingStyles.scopeLine} />
          <div className={sharingStyles.scopeOr}>oraz</div>
          <div className={sharingStyles.scopeLine} />
        </div>

        <div className={`${sharingStyles.scopeCard} ${sharePublic ? sharingStyles.active : ""}`}>
          <div className={sharingStyles.scopeIcon}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
            </svg>
          </div>
          <div className={sharingStyles.scopeTitle}>Udostępnienie publiczne</div>
          <div className={sharingStyles.scopeDesc}>Widoczna dla wszystkich użytkowników eSzyfrowane.pl i NotaUp</div>
        </div>
      </div>
    </div>
  );
};

const RecipientsPreview = ({
  settings,
  members,
}: {
  settings: IContactFormWizardSettings;
  members: IWorkspaceMember[];
}) => {
  const selected = members.filter(
    (m) => m.UserId && settings.ReceiverIds.includes(m.UserId),
  );
  const showAddress = settings.shareAddress && !!settings.companyAddress;

  return (
    <div className={bizStyles.recRoot}>
      <div className={bizStyles.recTitle}>
        Lista osób posiadających indywidualne skrzynki w poczcie eSzyfrowane
      </div>
      {selected.length === 0 ? (
        <div className={bizStyles.recEmpty}>
          Wybierz osoby z zespołu w panelu po prawej stronie
        </div>
      ) : (
        <div className={bizStyles.recGrid}>
          {selected.map((m) => (
            <div key={m.Id} className={bizStyles.recCard}>
              <div className={bizStyles.recCardStripe} />
              <div className={bizStyles.recCardBody}>
                <div className={bizStyles.recCardName}>{m.Label}</div>
                {settings.companyName && (
                  <div className={bizStyles.recCardCompany}>
                    {settings.companyName}
                  </div>
                )}
                {settings.nip && (
                  <div className={bizStyles.recCardNip}>NIP: {settings.nip}</div>
                )}
                {showAddress && (
                  <div className={bizStyles.recCardAddress}>
                    {settings.companyAddress}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SecretariatPreview = ({
  settings,
  members,
}: {
  settings: IContactFormWizardSettings;
  members: IWorkspaceMember[];
}) => {
  const selected = members.filter((m) =>
    settings.RegistryOfficeUserIds.includes(m.UserId),
  );

  return (
    <div className={bizStyles.secRoot}>
      <div className={bizStyles.secTitle}>
        Osoby, które będą obsługiwały skrzynkę z przesyłkami szyfrowanymi
      </div>
      {selected.length === 0 ? (
        <div className={bizStyles.secEmpty}>
          Wybierz osoby z zespołu w panelu po prawej stronie
        </div>
      ) : (
        <div className={bizStyles.secList}>
          {selected.map((m) => (
            <div key={m.Id} className={bizStyles.secRow}>
              <Avatar name={m.Label} size="small" />
              <div className={bizStyles.secInfo}>
                <div className={bizStyles.secName}>{m.Label}</div>
                {m.Email && (
                  <div className={bizStyles.secEmail}>{m.Email}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactFormWizardPreview = ({ settings, members = [], activeStep }: Props) => {
  const { t } = useTranslation();

  const themeClass = THEME_CLASSES[settings.Theme] ?? styles.theme0;

  const receivers = useMemo(() => {
    const list: { Id: string; Label: string; UserIds: string[] }[] = [];
    if (settings.RegistryOfficeUserIds?.length) {
      list.push({ Id: "regOffice", Label: "Sekretariat", UserIds: settings.RegistryOfficeUserIds });
    }
    members
      .filter((m) => settings.ReceiverIds?.includes(m.UserId))
      .forEach((m) => list.push({ Id: m.Id, Label: m.Label, UserIds: [] }));
    return list;
  }, [settings, members]);

  if (activeStep === EWizardStep.AddressBook) {
    return <BusinessCardPreview settings={settings} />;
  }

  if (activeStep === EWizardStep.Sharing) {
    return <SharingPreview settings={settings} />;
  }

  if (activeStep === EWizardStep.Secretariat) {
    return <SecretariatPreview settings={settings} members={members} />;
  }

  if (activeStep === EWizardStep.Recipients) {
    return <RecipientsPreview settings={settings} members={members} />;
  }

  return (
    <div className={styles.previewRoot}>
      <div className={classNames(styles.canvasBackground, themeClass)}>
        <div className={styles.bgl} />
        <div className={styles.bgr} />
      </div>
      <div className={styles.canvas}>
        {settings.LogoBlob && (
          <div className={styles.logo}>
            <img src={URL.createObjectURL(settings.LogoBlob)} alt="Logo" />
          </div>
        )}
        <div className={styles.canvasContent}>
          <div className={styles.centerSlot}>
            <div className={styles.formWrapper}>
              <Form
                inline
                id="eszyf-preview"
                settings={{
                  Theme: settings.Theme,
                  FieldStyle: settings.FieldStyle,
                  AccentColor: settings.AccentColor,
                  HeaderTitle: settings.HeaderTitle,
                  ShowHeader: settings.ShowHeader,
                  Receivers: receivers,
                }}
                demo
              />
            </div>
          </div>
        </div>
        <div className={styles.poweredBy}>
          <span>{t("contactFormWizard.poweredBy")}</span>
          <NotaIcon />
        </div>
      </div>
    </div>
  );
};

export default ContactFormWizardPreview;
